import { prudentialAnnualCheckInScript } from './prudential-annual-checkin';
import { prudentialAnnualCheckInScriptThai } from './prudential-annual-checkin-thai';
import { wealthLandscapeForumScript } from './wealth-landscape-forum';
import { chubbPaymentReminderScript } from './chubb-payment-reminder';
import { grabRestaurantOnboardingScript } from './grab-restaurant-onboarding';
import type { Script } from './prudential-annual-checkin';

export interface ScriptMetadata {
  title: string;
  company: string;
  description: string;
  color: string;
  product: string;
  features: string[];
}

export interface ScriptConfig {
  script: Script;
  metadata: ScriptMetadata;
}

export const scriptRegistry: Record<string, ScriptConfig> = {
  'prudential-annual-checkin': {
    script: prudentialAnnualCheckInScript,
    metadata: {
      title: 'Prudential Annual Check-In',
      company: 'Prudential',
      description: 'AI agent follows the exact Enhanced Annual Check-In script with intelligent branching based on customer responses.',
      color: '#0066cc',
      product: 'prushield',
      features: [
        'Follows exact script word-for-word',
        'Intelligent response analysis',
        'Dynamic flow branching',
        'Real-time progress tracking',
        'Handles all conversation paths',
      ]
    }
  },
  'wealth-landscape-forum': {
    script: wealthLandscapeForumScript,
    metadata: {
      title: 'Wealth Forum Invitation',
      company: 'WRISE',
      description: 'WRISE Wealth Landscape Forum invitation script for exclusive event promotion.',
      color: '#8B5A2B',
      product: 'wrise-group',
      features: [
        'Event invitation flow',
        'Guest registration handling',
        'Objection management',
        'Follow-up scheduling',
        'Value proposition emphasis',
      ]
    }
  },
  'grab-restaurant-onboarding': {
    script: grabRestaurantOnboardingScript,
    metadata: {
      title: 'Grab Restaurant Onboarding Call',
      company: 'Grab',
      description:
        'Scripted SDR flow to book a restaurant owner into a Grab account executive onboarding call.',
      color: '#00B14F',
      product: 'grab-merchant',
      features: [
        'Identity confirmation and time-respectful intro',
        'Value proposition tailored to Singapore restaurants',
        'Qualification branching for existing vs new partners',
        'Objection handling for time and competing platforms',
        'Meeting scheduling with positive and decline paths',
      ],
    },
  },
  'chubb-payment-reminder': {
    script: chubbPaymentReminderScript,
    metadata: {
      title: 'Chubb Life Insurance - Payment Reminder',
      company: 'Chubb Life Thailand',
      description: 'AI assistant calling on behalf of insurance advisor for annual premium payment reminder with Thai market payment options and promotions.',
      color: '#2E8B57',
      product: 'chubb-life',
      features: [
        'Annual premium payment reminder',
        'Thai payment channels (7-Eleven, eService)',
        'Auto-debit and banking options',
        'Promotion offerings',
        'Advisor callback scheduling',
      ]
    }
  },
  'prudential-annual-checkin-thai': {
    script: prudentialAnnualCheckInScriptThai, 
    metadata: {
      title: 'Prudential Annual Check-In (Thai)',
      company: 'Prudential',
      description: 'Thai version - AI agent follows the exact Enhanced Annual Check-In script with intelligent branching based on customer responses.',
      color: '#0066cc',
      product: 'prushield',
      features: [
        'Thai language script',
        'Follows exact script word-for-word',
        'Intelligent response analysis',
        'Dynamic flow branching',
        'Real-time progress tracking',
      ]
    }
  }
};

export const getScript = (scriptId: string): Script | null => {
  return scriptRegistry[scriptId]?.script || null;
};

export const getScriptMetadata = (scriptId: string): ScriptMetadata | null => {
  return scriptRegistry[scriptId]?.metadata || null;
};

export const getAllScripts = (): Array<{ id: string; config: ScriptConfig }> => {
  return Object.entries(scriptRegistry).map(([id, config]) => ({ id, config }));
};
